/**
 * Support messaging composable for the /messaging namespace.
 *
 * initSupport()  — call once from App.vue to register event listeners.
 * resetSupport() — call on logout to clean up.
 * useSupport()   — returns socket action functions for components.
 */
import { getNamespaceSocket } from './useSocket'
import { useSupportStore } from '@/stores/support.store'

let initialized = false

/**
 * Register support-specific socket listeners on the /messaging namespace.
 * Safe to call multiple times — only initializes once.
 */
export function initSupport() {
  if (initialized) return

  const socket = getNamespaceSocket('/messaging')
  if (!socket) return

  initialized = true
  const supportStore = useSupportStore()

  socket.on('support_new_message', payload => {
    supportStore.handleSupportNewMessage(payload)
  })
}

/**
 * Reset initialization state (called on logout).
 */
export function resetSupport() {
  initialized = false
  const supportStore = useSupportStore()
  supportStore.$reset()
}

/**
 * Returns action functions for support messaging.
 */
export function useSupport() {
  function sendMessageViaSocket(text, imageIds) {
    return new Promise((resolve, reject) => {
      const socket = getNamespaceSocket('/messaging')
      if (!socket?.connected) {
        reject(new Error('Not connected'))
        
        return
      }
      socket.emit('support_send_message', { text, imageIds }, response => {
        if (response.error) reject(new Error(response.error))
        else resolve(response.message)
      })
    })
  }

  function sendAdminMessageViaSocket(conversationId, text, imageIds) {
    return new Promise((resolve, reject) => {
      const socket = getNamespaceSocket('/messaging')
      if (!socket?.connected) {
        reject(new Error('Not connected'))
        
        return
      }
      socket.emit('support_admin_send_message', { conversationId, text, imageIds }, response => {
        if (response.error) reject(new Error(response.error))
        else resolve(response.message)
      })
    })
  }

  function viewConversation(conversationId) {
    const socket = getNamespaceSocket('/messaging')
    if (socket?.connected) {
      socket.emit('support_viewing_conversation', { conversationId })
    }
  }

  function leaveConversation() {
    const socket = getNamespaceSocket('/messaging')
    if (socket?.connected) {
      socket.emit('support_leave_conversation')
    }
  }

  function markRead(conversationId) {
    const socket = getNamespaceSocket('/messaging')
    if (socket?.connected) {
      socket.emit('support_mark_read', { conversationId })
    }
  }

  function markUnread(conversationId) {
    const socket = getNamespaceSocket('/messaging')
    if (socket?.connected) {
      socket.emit('support_mark_unread', { conversationId })
    }
  }

  return {
    sendMessageViaSocket,
    sendAdminMessageViaSocket,
    viewConversation,
    leaveConversation,
    markRead,
    markUnread,
  }
}
